import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  LinearProgress,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import LanguageMarquee from './common/LanguageMarquee';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Api as ApiIcon,
  Lock as LockIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getSttClient } from '../api/axiosClients';

// Create a client for demo endpoints without auth
const demoClient = getSttClient();

const Demo = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [logoErrors, setLogoErrors] = useState({
    fasterWhisper: false,
    nvidia: false,
    fastapi: false,
    celery: false,
    mcp: false,
    grafana: false,
    loki: false,
    promtail: false,
  });
  const [transcription, setTranscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [pollInterval, setPollInterval] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    setHeroVisible(true);
    return () => {
      clearInterval(timerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const ext = mimeType.includes('webm') ? 'webm' : 'ogg';
        const recordedFile = new File([blob], `recording.${ext}`, { type: mimeType });
        setFile(recordedFile);
        setError(null);
        setTranscription(null);
        setJobId(null);
        setStatus(null);
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch (err) {
      if (!window.isSecureContext) {
        setError('Microphone requires a secure connection (HTTPS). On local network, open the app via https:// or use localhost instead of an IP address.');
      } else if (err.name === 'NotAllowedError') {
        setError('Microphone access was denied. Please allow microphone permission in your browser and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else {
        setError('Microphone is not available. Please check your browser permissions.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (selectedFile.size > maxSize) {
        setError('File size limit: 10MB for demo');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
      setTranscription(null);
      setJobId(null);
      setStatus(null);
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setTranscription(null);
    setJobId(null);
    setStatus(null);
    setError(null);
    // Reset file input
    const fileInput = document.getElementById('demo-file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const selectedFile = e.dataTransfer.files[0];
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (selectedFile.size > maxSize) {
        setError('File size limit: 10MB for demo');
        return;
      }
      setFile(selectedFile);
      setError(null);
      setTranscription(null);
      setJobId(null);
      setStatus(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select an audio file');
      return;
    }

    setLoading(true);
    setError(null);
    setTranscription(null);
    setProgress(0);
    setStatus(null);

    if (pollInterval) {
      clearInterval(pollInterval);
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('priority', '5');

      const response = await demoClient.post('/api/demo/transcribe', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        auth: false,
      });

      setJobId(response.data.id);
      setStatus(response.data.status);
      setProgress(response.data.progress || 0);

      startPolling(response.data.id);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to upload file');
      setLoading(false);
    }
  };

  const startPolling = (id) => {
    const interval = setInterval(async () => {
      try {
        const response = await demoClient.get(`/api/demo/${id}/status`, {
          auth: false,
        });
        const jobStatus = response.data;

        setStatus(jobStatus.status);
        setProgress(jobStatus.progress || 0);

        if (jobStatus.status === 'completed') {
          clearInterval(interval);
          setPollInterval(null);
          fetchResult(id);
          setLoading(false);
        } else if (jobStatus.status === 'failed') {
          clearInterval(interval);
          setPollInterval(null);
          setError(jobStatus.error_message || 'Transcription failed');
          setLoading(false);
        }
      } catch (err) {
        clearInterval(interval);
        setPollInterval(null);
        setError('Failed to check job status');
        setLoading(false);
      }
    }, 2000);

    setPollInterval(interval);
  };

  const fetchResult = async (id) => {
    try {
      const response = await demoClient.get(`/api/demo/${id}/result`, {
        auth: false,
      });
      setTranscription(response.data.transcription);
    } catch (err) {
      setError('Failed to fetch transcription result');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
      }}
    >
      {/* Background gradient overlay for entire page */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 0%, rgba(25, 118, 210, 0.15) 0%, transparent 50%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100vw',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          pt: { xs: 16, md: 20 },
          pb: { xs: 12, md: 16 },
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
          zIndex: 1,
        }}
      >
        {/* Hero-specific gradient overlay - full width */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            background: 'radial-gradient(circle at 50% 0%, rgba(25, 118, 210, 0.15) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
        {/* Full-width content wrapper - wider than other sections */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            px: { xs: 4, sm: 5, md: 6, lg: 10, xl: 14 },
            mx: 'auto',
          }}
        >
          <Box
            sx={{
              textAlign: 'left',
              position: 'relative',
              zIndex: 1,
              width: '100%',
            }}
          >
            {/* Main Heading */}
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '3rem', sm: '4rem', md: '5.5rem', lg: '6.5rem', xl: '7rem' },
                fontWeight: 800,
                lineHeight: 1.05,
                mb: { xs: 2.5, md: 3.5 },
                background: 'linear-gradient(135deg, #ffffff 0%, #a0a0a0 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.04em',
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
                transitionDelay: '0.1s',
              }}
            >
              Transcribe hours of audio
            </Typography>

            {/* Additional text */}
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1.125rem', md: '1.375rem', lg: '1.5rem' },
                color: 'rgba(255, 255, 255, 0.7)',
                mb: { xs: 4, md: 6 },
                fontWeight: 400,
                lineHeight: 1.5,
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
                transitionDelay: '0.5s',
              }}
            >
              Into clean, usable text.
            </Typography>

            {/* Tagline */}
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '0.875rem', md: '1rem' },
                color: 'rgba(255, 255, 255, 0.55)',
                fontWeight: 500,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
                transitionDelay: '0.7s',
              }}
            >
              Upload audio. Get text. Done.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {[
              {
                icon: <SpeedIcon sx={{ fontSize: 40 }} />,
                title: 'Lightning Fast',
                description: 'Get transcriptions in minutes, not hours. Built for speed without sacrificing accuracy.',
              },
              {
                icon: <LockIcon sx={{ fontSize: 40 }} />,
                title: 'Your Data Stays Yours',
                description: 'No data used for training. Your audio is processed and deleted—never shared or learned from.',
              },
              {
                icon: <SecurityIcon sx={{ fontSize: 40 }} />,
                title: 'Secure & Private',
                description: 'Self-hosted servers in the European Union. Your audio files are processed securely and never stored permanently.',
              },
              {
                icon: <ApiIcon sx={{ fontSize: 40 }} />,
                title: 'Predictable Pricing',
                description: 'No per-request surprises. Simple, transparent pricing that scales with your needs.',
              },
              {
                icon: <TranslateIcon sx={{ fontSize: 40 }} />,
                title: '99 Languages',
                description: 'Automatically detects and transcribes audio in 99 languages. No configuration needed—just upload and go.',
              },
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: 'background.paper',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    p: 3,
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                    },
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Language Marquee */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" disableGutters>
          <LanguageMarquee />
        </Container>
      </Box>

      {/* Demo Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, position: 'relative', zIndex: 1 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Try it now
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontWeight: 400,
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Upload an audio file and see the transcription in action
            </Typography>
          </Box>
          {/* Selected File Display */}
          {file && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                bgcolor: 'rgba(26, 26, 26, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight="medium" color="text.primary">
                  Selected file: {file.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<CloseIcon />}
                onClick={handleClearFile}
                disabled={loading}
              >
                Remove
              </Button>
            </Paper>
          )}

          {/* Upload Area */}
          <Paper
            elevation={0}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              p: { xs: 4, md: 6 },
              border: `2px dashed ${dragActive ? 'primary.main' : 'rgba(255, 255, 255, 0.2)'}`,
              borderRadius: 2,
              textAlign: 'center',
              bgcolor: 'rgba(26, 26, 26, 0.8)',
              borderColor: file ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'rgba(25, 118, 210, 0.1)',
              },
            }}
            onClick={() => document.getElementById('demo-file-input').click()}
          >
            <input
              accept="audio/*"
              style={{ display: 'none' }}
              id="demo-file-input"
              type="file"
              onChange={handleFileSelect}
              disabled={loading}
            />
            <CloudUploadIcon
              sx={{
                fontSize: 64,
                mb: 2,
                color: dragActive ? 'primary.main' : 'text.secondary',
              }}
            />
            <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
              {file ? 'Click to select a different file' : 'Drop audio file here or click to browse'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Supported formats: MP3, WAV, OGG, WebM, M4A, FLAC, AAC
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<MicIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleUpload();
              }}
              disabled={!file || loading}
              sx={{
                mt: 2,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {loading ? 'Processing...' : 'TRANSCRIBE AUDIO'}
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mt: 1.5,
                display: 'block',
                fontSize: '0.75rem',
              }}
            >
              Want to automate this? →{' '}
              <Typography
                component="a"
                href="/api/stt/docs"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Check the API docs
              </Typography>
            </Typography>
          </Paper>

          {/* Compact record bar */}
          <Box
            sx={{
              mt: 1.5,
              px: 2,
              py: 1.25,
              borderRadius: 2,
              border: '1px solid',
              borderColor: isRecording ? 'rgba(211,47,47,0.4)' : 'rgba(255,255,255,0.08)',
              bgcolor: isRecording ? 'rgba(211,47,47,0.05)' : 'rgba(26,26,26,0.6)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              transition: 'all 0.3s',
            }}
          >
            {!isRecording ? (
              <>
                <Typography variant="body2" color="text.disabled" sx={{ flex: 1 }}>
                  Or record from microphone
                </Typography>
                {window.isSecureContext ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MicIcon sx={{ fontSize: 16 }} />}
                    onClick={startRecording}
                    disabled={loading}
                    sx={{
                      textTransform: 'none',
                      borderColor: 'rgba(255,255,255,0.2)',
                      color: 'text.secondary',
                      fontSize: '0.8rem',
                      py: 0.5,
                      '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
                    }}
                  >
                    Record
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    HTTPS required
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    flexShrink: 0,
                    '@keyframes pulse': {
                      '0%': { boxShadow: '0 0 0 0 rgba(211,47,47,0.7)' },
                      '70%': { boxShadow: '0 0 0 7px rgba(211,47,47,0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(211,47,47,0)' },
                    },
                    animation: 'pulse 1.4s ease-in-out infinite',
                  }}
                />
                <Typography variant="body2" color="error.main" fontWeight={600} sx={{ flex: 1 }}>
                  {formatTime(recordingTime)}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<StopIcon sx={{ fontSize: 16 }} />}
                  onClick={stopRecording}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    py: 0.5,
                  }}
                >
                  Stop
                </Button>
              </>
            )}
          </Box>

          {/* Demo Limits Box */}
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 2,
              bgcolor: 'rgba(26, 26, 26, 0.8)',
              borderLeft: '4px solid',
              borderColor: 'warning.main',
              borderRadius: 1,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Demo limits:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Max 30 seconds audio duration
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Max 10MB file size
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  3 requests per hour per IP address
                </Typography>
              </li>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {loading && status && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Status: <strong>{status}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {progress}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            )}
          </Paper>

          {/* Transcription Result */}
          {transcription && (
            <Box sx={{ mt: { xs: 2, sm: 4 } }}>
              {/* Transcription Result Header */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                mb: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 },
              }}>
                <Typography 
                  variant="h6" 
                  color="success.main" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    textAlign: { xs: 'center', sm: 'left' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  ✓ Transcription Result
                </Typography>
                <Tooltip title={copySuccess ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton
                    size="small"
                    onClick={async () => {
                      const textToCopy =
                        typeof transcription === 'string'
                          ? transcription
                          : typeof transcription.text === 'string' ? transcription.text : JSON.stringify(transcription);

                      try {
                        if (window.isSecureContext && navigator.clipboard?.writeText) {
                          await navigator.clipboard.writeText(textToCopy);
                        } else {
                          const textArea = document.createElement('textarea');
                          textArea.value = textToCopy;
                          textArea.style.position = 'fixed';
                          textArea.style.top = '0';
                          textArea.style.left = '0';
                          textArea.style.width = '1px';
                          textArea.style.height = '1px';
                          textArea.style.opacity = '0';
                          document.body.appendChild(textArea);
                          textArea.focus();
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                        setCopySuccess(true);
                        setTimeout(() => setCopySuccess(false), 2000);
                      } catch (err) {
                        console.error('Failed to copy:', err);
                      }
                    }}
                    sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' } }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Transcription Result Content */}
              <Paper
                sx={{
                  p: { xs: 1.5, sm: 3 },
                  bgcolor: 'rgba(26, 26, 26, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  mb: 2,
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                    fontFamily: 'monospace',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    color: 'text.primary',
                    overflowWrap: 'break-word',
                  }}
                >
                  {typeof transcription === 'string'
                    ? transcription
                    : typeof transcription.text === 'string' ? transcription.text : JSON.stringify(transcription)}
                </Typography>
              </Paper>

              {/* Info Box */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'rgba(26, 26, 26, 0.8)',
                  borderLeft: '4px solid',
                  backdropFilter: 'blur(10px)',
                  borderColor: 'info.main',
                  borderRadius: 1,
                }}
              >
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  This is the raw transcription output. Register to get more features, longer audio
                  support, and API access.
                </Typography>
              </Paper>
            </Box>
          )}
        </Container>
      </Box>

      {/* Powered By Section */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          position: 'relative',
          zIndex: 1,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                mb: 3,
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              Powered by
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: { xs: 3, md: 5 },
                opacity: 0.7,
                '&:hover': {
                  opacity: 1,
                },
                transition: 'opacity 0.3s',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontWeight: 500,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                  }}
                >
                  Faster Whisper
                </Typography>
              </Box>
              <Box sx={{ width: '1px', height: '20px', bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!logoErrors.nvidia ? (
                  <Box
                    component="img"
                    src="https://upload.wikimedia.org/wikipedia/sco/thumb/2/21/Nvidia_logo.svg/200px-Nvidia_logo.svg.png"
                    alt="NVIDIA"
                    sx={{
                      height: { xs: 20, md: 24 },
                      width: 'auto',
                      filter: 'brightness(0) invert(1)',
                    }}
                    onError={() => setLogoErrors(prev => ({ ...prev, nvidia: true }))}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: 500,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                    }}
                  >
                    NVIDIA
                  </Typography>
                )}
              </Box>
              <Box sx={{ width: '1px', height: '20px', bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontWeight: 500,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                  }}
                >
                  TUSD
                </Typography>
              </Box>
              <Box sx={{ width: '1px', height: '20px', bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!logoErrors.mcp ? (
                  <>
                    <Box
                      component="img"
                      src="https://modelcontextprotocol.io/favicon.ico"
                      alt="MCP"
                      sx={{
                        height: { xs: 20, md: 22 },
                        width: 'auto',
                        filter: 'brightness(0) invert(1)',
                      }}
                      onError={() => setLogoErrors(prev => ({ ...prev, mcp: true }))}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: 500,
                        fontSize: { xs: '0.9rem', md: '1rem' },
                      }}
                    >
                      MCP
                    </Typography>
                  </>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: 500,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                    }}
                  >
                    MCP
                  </Typography>
                )}
              </Box>
              <Box sx={{ width: '1px', height: '20px', bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!logoErrors.fastapi ? (
                  <Box
                    component="img"
                    src="https://fastapi.tiangolo.com/img/logo-margin/logo-teal.png"
                    alt="FastAPI"
                    sx={{
                      height: { xs: 24, md: 28 },
                      width: 'auto',
                    }}
                    onError={() => setLogoErrors(prev => ({ ...prev, fastapi: true }))}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: 500,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                    }}
                  >
                    FastAPI
                  </Typography>
                )}
              </Box>
              <Box sx={{ width: '1px', height: '20px', bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!logoErrors.celery ? (
                  <>
                    <Box
                      component="img"
                      src="https://docs.celeryq.dev/en/stable/_static/celery_512.png"
                      alt="Celery"
                      sx={{
                        height: { xs: 24, md: 28 },
                        width: 'auto',
                        filter: 'brightness(0) invert(1)',
                      }}
                      onError={() => setLogoErrors(prev => ({ ...prev, celery: true }))}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: 500,
                        fontSize: { xs: '0.9rem', md: '1rem' },
                      }}
                    >
                      Celery
                    </Typography>
                  </>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: 500,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                    }}
                  >
                    Celery
                  </Typography>
                )}
              </Box>
              <Box sx={{ width: '1px', height: '20px', bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!logoErrors.grafana ? (
                  <>
                    <Box
                      component="img"
                      src="https://grafana.com/static/img/logos/grafana_logo_swirl_fullcolor.svg"
                      alt="Grafana"
                      sx={{
                        height: { xs: 24, md: 28 },
                        width: 'auto',
                      }}
                      onError={() => setLogoErrors(prev => ({ ...prev, grafana: true }))}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: 500,
                        fontSize: { xs: '0.9rem', md: '1rem' },
                      }}
                    >
                      Grafana
                    </Typography>
                  </>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: 500,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                    }}
                  >
                    Grafana
                  </Typography>
                )}
              </Box>
              <Box sx={{ width: '1px', height: '20px', bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontWeight: 500,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                  }}
                >
                  Loki
                </Typography>
              </Box>
              <Box sx={{ width: '1px', height: '20px', bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontWeight: 500,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                  }}
                >
                  Promtail
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          position: 'relative',
          zIndex: 1,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
                fontSize: { xs: '1.75rem', md: '2.25rem' },
              }}
            >
              Ready to get started?
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4, fontWeight: 400 }}>
              Sign up and start transcribing your audio files today
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                sx={{
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                }}
              >
                Sign up
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                Log in
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

    </Box>
  );
};

export default Demo;
